import {OrderDto, OrderStatus, ShippingType} from '../../types'

import {LifeCycleEvent} from './lifeCycle.types'
import {allRelevantDeliveriesInvoiced, isLastTruckLoaded} from './lifeCycleHelpers'

/**
 * Maps order status to base lifecycle event
 */
const statusToBaseMoment: Record<OrderStatus, LifeCycleEvent> = {
  [OrderStatus.Cancelled]: LifeCycleEvent.Canceled,
  [OrderStatus.Rejected]: LifeCycleEvent.Canceled,

  [OrderStatus.Pending]: LifeCycleEvent.PrePlanning,
  [OrderStatus.Confirmed]: LifeCycleEvent.PrePlanning,
  [OrderStatus.Reserved]: LifeCycleEvent.PrePlanning,
  [OrderStatus.Backorder]: LifeCycleEvent.PrePlanning,
  [OrderStatus.PaymentPending]: LifeCycleEvent.PrePlanning,

  [OrderStatus.InProgress]: LifeCycleEvent.Started,
  [OrderStatus.PartiallyDelivered]: LifeCycleEvent.Started,
  [OrderStatus.PartiallyShipped]: LifeCycleEvent.Started,

  [OrderStatus.Shipped]: LifeCycleEvent.Started,
  [OrderStatus.Delivered]: LifeCycleEvent.Delivered,

  [OrderStatus.Closed]: LifeCycleEvent.Unknown
}

/**
 * Determines the current lifecycle event for an order based on its status,
 * delivery progress, and other factors
 *
 * @param order - Order to evaluate
 * @param now - Function to get current date (for testing)
 * @returns The current lifecycle event
 */
export const getLifeCycleEvent = (
  order: OrderDto,
  now: () => Date = () => new Date()
): LifeCycleEvent => {
  const base = statusToBaseMoment[order.orderStatus] ?? LifeCycleEvent.Unknown

  if (base < LifeCycleEvent.PrePlanning) {
    return base
  }

  if (
    (order.orderStatus === OrderStatus.Shipped || order.orderStatus === OrderStatus.Delivered) &&
    allRelevantDeliveriesInvoiced(order)
  ) {
    return LifeCycleEvent.Invoiced
  }

  if (order.orderStatus === OrderStatus.Shipped && order.shippingType === ShippingType.Collect) {
    return LifeCycleEvent.Delivered
  }

  if (base === LifeCycleEvent.Started && isLastTruckLoaded(order)) {
    return LifeCycleEvent.LastTruckLoaded
  }

  if (base === LifeCycleEvent.PrePlanning && new Date(order.shippingDate) <= now()) {
    return LifeCycleEvent.Started
  }

  return base
}
